import { useState } from 'react'
import {
  CurecallButton,
  CurecallPasswordInput,
  CurecallModalContainer,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { TwoFactorService } from 'src/services/two-factor.service'
import TwoFactorBackupCodes from './two-factor-backup-codes.component'

type Props = {
  isOpen: boolean
  onClose: () => void
  onRegenerated: () => void
}

export default function TwoFactorRegenerateCodesModal({ isOpen, onClose, onRegenerated }: Props) {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)

  const handleRegenerate = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await TwoFactorService.regenerateBackupCodes(password)
      setBackupCodes(result.backupCodes)
    } catch {
      setError(t('pages.auth.account.two-factor.wrong-password'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcknowledge = () => {
    notify({
      title: t('common.actions.success'),
      message: t('pages.auth.account.two-factor.codes-regenerated'),
      appearance: ECurecallNotificationAppearance.SUCCESS,
    })
    onRegenerated()
    onClose()
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      title={t('pages.auth.account.two-factor.regenerate-title')}
      isOpen={isOpen}
      toggle={onClose}
      dialogClassName="sm:c-w-[500px]"
    >
      {!backupCodes ? (
        <div className="c-flex c-flex-col c-gap-6">
          <p className="c-text-sm c-text-gray-600">{t('pages.auth.account.two-factor.regenerate-warning')}</p>

          <CurecallPasswordInput
            label={t('pages.auth.input.password')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              setError(null)
            }}
            errorMessage={error ?? undefined}
            isRequired
            autoFocus
          />

          <div className="c-flex c-justify-center c-gap-4">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onClose}>
              {t('common.actions.cancel')}
            </CurecallButton>
            <CurecallButton onClick={handleRegenerate} isLoading={isLoading} isDisabled={!password}>
              {t('pages.auth.account.two-factor.regenerate-confirm')}
            </CurecallButton>
          </div>
        </div>
      ) : (
        <TwoFactorBackupCodes backupCodes={backupCodes} onAcknowledge={handleAcknowledge} />
      )}
    </CurecallModalContainer>
  )
}
